import {
  addDays,
  eachDayOfInterval,
  format,
  max,
  startOfTomorrow,
  parseISO,
  isBefore,
  setHours,
  startOfDay,
  setMinutes,
  eachHourOfInterval,
} from 'date-fns';
import {
  zonedTimeToUtc,
  getTimezoneOffset,
  formatInTimeZone,
} from 'date-fns-tz';
import { uniqBy, groupBy } from 'es-toolkit/compat';
import { TourRequestCreateModel, TourRequestModel } from 'src/api/models';
import { HOUSE_PAGE_STRINGS } from 'src/features/house-page/house-page.strings';
import { array, object, Infer, string, refine } from 'superstruct';

export type TourAvailabilityFormValues = {
  availability: {
    date: string;
    timeRanges: {
      startOn: string;
      endOn: string;
    }[];
  }[];
};

const limitHours = {
  start: 9,
  end: 19,
};

// Get the following 7 days, optionally starting from minDate (yyyy-MM-dd)
export const getDateOptions = (
  availabilityFormValues: TourAvailabilityFormValues['availability'],
  dateIndex: number,
  minDate?: string,
) => {
  // `!`: `dateIndex` is the index of an entry the caller is already rendering.
  const selectedDateString = availabilityFormValues[dateIndex]!.date;
  const alreadySelectedDates =
    availabilityFormValues?.map((option) => option.date) ?? [];

  const startDate = minDate
    ? max([startOfTomorrow(), parseISO(minDate)])
    : startOfTomorrow();

  const optionsIncludingSelected = [
    // parseIso completes the missing data with the user timezone
    // so when we do the format we get the correct date
    ...(selectedDateString ? [parseISO(selectedDateString)] : []),
    ...eachDayOfInterval({
      start: startDate,
      end: addDays(startDate, 6),
    }),
  ].map((date) => ({
    label: format(date, 'EEE MMM d, yyyy'),
    value: format(date, 'yyyy-MM-dd'),
  }));

  return (
    uniqBy(optionsIncludingSelected, 'value')
      // prevent repeating dates
      // (remove all already selected dates except the selected one for the current input)
      .filter(
        (option) =>
          option.value === selectedDateString ||
          !alreadySelectedDates.includes(option.value),
      )
  );
};

// Generate numbers based on limitHours and format them to AM/PM format
export const getTimeOptions = ({
  timeZone,
  startTime,
  type,
  currentValue,
}: {
  /** Pass startTime for end of time range options so we only show options after the start time */
  startTime?: string; // HH:mm format
  timeZone: string;
  type: 'start' | 'end';
  currentValue?: string;
}) => {
  // We always use the current day to render the options
  const currentDateWithoutTime = startOfDay(new Date());

  const getOptionFromDate = (time: Date) => {
    const timeZoneString = formatInTimeZone(new Date(), timeZone, 'zzz');

    return {
      value: format(time, 'HH:mm'),
      label: `${format(time, 'p')} ${timeZoneString}`,
    };
  };

  // In some weird cases the currentValue might be outside the range of options
  // so we need to add it at the beginning of the options array
  // (if we don't the dropdown will be empty)
  const [currentValueHours, currentValueMinutes] =
    currentValue?.split(':').map(Number) ?? [];
  const currentValueDate =
    currentValueHours !== undefined && currentValueMinutes !== undefined
      ? setMinutes(
          setHours(currentDateWithoutTime, currentValueHours),
          currentValueMinutes,
        )
      : undefined;

  const intervalOptions = eachHourOfInterval({
    start: setHours(currentDateWithoutTime, limitHours.start),
    end: setHours(currentDateWithoutTime, limitHours.end),
  })
    // add half hour options
    .flatMap((oClockOption, optionIndex, options) => {
      const halfHourOption = setMinutes(oClockOption, 30);
      const isLastOption = optionIndex === options.length - 1;

      // Remove the half hour option for the last option
      return [oClockOption, ...(isLastOption ? [] : [halfHourOption])];
    })
    .filter((timeOption) => {
      const [timeHours, timeMinutes] = format(timeOption, 'HH:mm')
        .split(':')
        .map(Number);
      const [startTimeHours, startTimeMinutes] =
        startTime?.split(':').map(Number) ?? [];

      // `!` on the split-and-map results: destructuring an array yields
      // `number | undefined` per element, and every comparison below already
      // treated them as numbers.
      const shouldFilterOption =
        // For start options remove options equal to the end limit (e.g. remove 19:00)
        (type === 'start' && timeHours! >= limitHours.end) ||
        // For end options remove options equal to the start limit (e.g. remove 09:00)
        (type === 'end' &&
          timeHours === limitHours.start &&
          timeMinutes === 0) ||
        // Remove end options before or equal to start time
        (type === 'end' &&
          startTime &&
          (timeHours! < startTimeHours! ||
            (timeHours === startTimeHours &&
              timeMinutes! <= startTimeMinutes!)));

      return !shouldFilterOption;
    });

  const options = intervalOptions
    .concat(
      currentValueDate &&
        !intervalOptions.find(
          (opt) => opt.getTime() === currentValueDate.getTime(),
        )
        ? [currentValueDate]
        : [],
    )
    .sort((a, b) => a.getTime() - b.getTime())
    .map(getOptionFromDate);

  return options;
};

const isValidDate = (value: string) => {
  const date = new Date(value);
  return !isNaN(date.getTime());
};

export const DatesQueryParamStruct = array(
  object({
    from: refine(string(), 'ValidDate', isValidDate),
    to: refine(string(), 'ValidDate', isValidDate),
  }),
);

export const transforms = {
  formToApi: ({
    formValues,
    timeZone,
  }: {
    formValues: TourAvailabilityFormValues;
    timeZone: string;
  }): Required<Pick<TourRequestCreateModel, 'availability'>> => ({
    ...formValues,
    availability: formValues.availability.flatMap(({ date, timeRanges }) =>
      timeRanges
        // sort by asc date
        .sort((a, b) => {
          const aTime = new Date(`${date} ${a.startOn}`).getTime();
          const bTime = new Date(`${date} ${b.startOn}`).getTime();

          return aTime - bTime;
        })
        .map((timeRange) => {
          return {
            // transform the selected hours to utc
            startOn: zonedTimeToUtc(
              `${date} ${timeRange.startOn}`,
              timeZone,
            ).toISOString(),
            endOn: zonedTimeToUtc(
              `${date} ${timeRange.endOn}`,
              timeZone,
            ).toISOString(),
          };
        }),
    ),
  }),
  /** Transform api model to form values
   * Map over an empty array to make sure that we always
   * have exactly the required dates length
   */
  apiToForm: ({
    model,
    timeZone,
  }: {
    model?: TourRequestModel;
    timeZone: string;
  }): TourAvailabilityFormValues => {
    const timeSlotsByDate = groupBy(
      // reorder dates to show them from most recent to oldest
      model?.availability?.sort(
        (a, b) => new Date(a.startOn).getTime() - new Date(b.startOn).getTime(),
      ) ?? [],
      (item) => format(new Date(item.startOn), 'yyyy-MM-dd'),
    );
    const selectedDates = Object.keys(timeSlotsByDate);

    return {
      availability: selectedDates.map((selectedDate) => {
        return {
          date: selectedDate,
          // `!`: `selectedDates` is `Object.keys` of this same grouping.
          timeRanges: timeSlotsByDate[selectedDate]!.map((timeRange) => {
            const isRangeInverted =
              new Date(timeRange.startOn).getTime() >
              new Date(timeRange.endOn).getTime();
            const startOn = isRangeInverted
              ? timeRange.endOn
              : timeRange.startOn;
            const endOn = isRangeInverted ? timeRange.startOn : timeRange.endOn;

            return {
              startOn: formatInTimeZone(new Date(startOn), timeZone, 'HH:mm'),
              endOn: formatInTimeZone(new Date(endOn), timeZone, 'HH:mm'),
            };
          }),
        };
      }),
    };
  },
  queryParamToForm: ({
    queryParam,
    timeZone,
  }: {
    queryParam: Infer<typeof DatesQueryParamStruct>;
    timeZone: string;
  }) => {
    const timeSlotsByDate = groupBy(queryParam, (item) =>
      format(new Date(item.from), 'yyyy-MM-dd'),
    );
    const selectedDates = Object.keys(timeSlotsByDate);

    return {
      availability: selectedDates.map((date) => ({
        // `!`: `selectedDates` is `Object.keys` of this same grouping, and
        // `groupBy` never produces an empty group.
        date: format(new Date(timeSlotsByDate[date]![0]!.from), 'yyyy-MM-dd'),
        timeRanges: timeSlotsByDate[date]!.map((timeSlot) => {
          return {
            startOn: formatInTimeZone(
              new Date(timeSlot.from),
              timeZone,
              'HH:mm',
            ),
            endOn: formatInTimeZone(new Date(timeSlot.to), timeZone, 'HH:mm'),
          };
        }),
      })),
    };
  },
};

export const isFutureDate = (timeZone: string) => (value: string) => {
  // get the start of tomorrow (in the home timezone)
  const limitDate = zonedTimeToUtc(startOfTomorrow(), timeZone);

  // transform UTC date to home timezone
  const tzOffset = getTimezoneOffset(timeZone);
  const valueDate = new Date(new Date(value).getTime() - tzOffset);

  if (isBefore(valueDate, limitDate)) {
    return HOUSE_PAGE_STRINGS['tour.passedDatesValidation'];
  }
};

export const isOnOrAfterDate = (minDate: string) => (value: string) => {
  if (!value) return;
  if (isBefore(parseISO(value), parseISO(minDate))) {
    return HOUSE_PAGE_STRINGS['tour.comingSoonDateValidation'];
  }
};
